import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

export function useStudentConversation(
  studentProfileId: string,
  createIfMissing: boolean = true
): {
  conversationId: string | null;
  unreadStudent: number;
  isLoading: boolean;
  error: Error | null;
} {
  const { data, isLoading, error } = useQuery({
    queryKey: ['student-conversation', studentProfileId],
    queryFn: async () => {
      // Get the student's personal_id from the students table
      const { data: studentRow, error: studentError } = await supabase
        .from('students')
        .select('personal_id')
        .eq('profile_id', studentProfileId)
        .maybeSingle();
      if (studentError) throw studentError;
      if (!studentRow) return null;

      const personalId = (studentRow as { personal_id: string }).personal_id;

      // Find existing conversation
      const { data: existing } = await supabase
        .from('conversations')
        .select('id, unread_student')
        .eq('student_id', studentProfileId)
        .eq('personal_id', personalId)
        .maybeSingle();

      if (existing) return existing as { id: string; unread_student: number };
      if (!createIfMissing) return null;

      // Create if not found
      const { data: created, error: createError } = await supabase
        .from('conversations')
        .insert({ student_id: studentProfileId, personal_id: personalId })
        .select('id, unread_student')
        .single();
      if (createError) throw createError;
      return created as { id: string; unread_student: number };
    },
    enabled: !!studentProfileId,
    staleTime: Infinity,
  });

  return {
    conversationId: data?.id ?? null,
    unreadStudent: data?.unread_student ?? 0,
    isLoading,
    error: error as Error | null,
  };
}
